import { useState, useRef, useEffect, type FormEvent } from 'react';
import { Modal } from './Modal';
import { FormInput } from './FormInput';
import { ErrorAlert } from './ErrorAlert';
import { LoadingButton } from './LoadingButton';
import { Button } from './Button';
import { useLanguage } from '../contexts/LanguageContext';
import { useSquad } from '../contexts/SquadContext';
import { useToast } from '../contexts/ToastContext';

interface JoinSquadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function JoinSquadModal({ isOpen, onClose }: JoinSquadModalProps) {
  const { t } = useLanguage();
  const { joinSquad } = useSquad();
  const { showToast } = useToast();
  const [squadCode, setSquadCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!squadCode.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: joinError } = await joinSquad(squadCode.trim());
      
      if (joinError) {
        setError(joinError.message);
        setLoading(false);
      } else if (data) {
        setLoading(false);
        showToast(t('squad.joinSuccess'), 'success');
        // Close modal after showing success
        timeoutRef.current = setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('common.unexpectedError');
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSquadCode('');
    setError(null);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t("league.join")}
        subtitle={t("league.codePlaceholder")}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <FormInput
            label={t("league.code")}
            value={squadCode}
            onChange={(value) => setSquadCode(value.toUpperCase())}
            placeholder={t("league.codePlaceholder")}
            maxLength={6}
            disabled={loading}
            required
          />

          {error && <ErrorAlert message={error} />}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              className="flex-1 !py-3"
              onClick={handleClose}
              disabled={loading}
            >
              {t("common.cancel")}
            </Button>
            <LoadingButton
              type="submit"
              variant="primary"
              loading={loading}
              loadingText={t("common.loading")}
              disabled={!squadCode.trim()}
              className="flex-1 !py-3"
            >
              {t("league.join")}
            </LoadingButton>
          </div>
        </form>
      </Modal>
    </>
  );
}

